import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/icons";
import { EmptyAction, EmptyState, Notice } from "../components/ui";
import { loadRpc } from "../lib/platform";

const ranges = [
  ["today", "Today"],
  ["7", "7 days"],
  ["30", "30 days"],
];

const cards = [
  ["clients", "Clients", "/clients", "bg-sky-50 text-brand-blue", (data) => data.clients_total, (data) => `${data.clients_active} active · ${data.clients_pending} pending · ${data.clients_suspended} suspended`],
  ["sites", "Sites", "/sites", "bg-emerald-50 text-emerald-600", (data) => data.sites_total, (data) => `${data.sites_active} active`],
  ["kiosks", "Kiosks", "/kiosks", "bg-violet-50 text-violet-600", (data) => data.kiosks_total, (data) => `${data.kiosks_online} online · ${data.kiosks_offline} offline · ${data.kiosks_disabled} disabled`],
  ["visits", "Visits on site", "/visits", "bg-amber-50 text-amber-600", (data) => data.visits_checked_in, (data) => `${data.visits_expected} approved or invited`],
  ["email", "Email queue", "/email", "bg-rose-50 text-rose-600", (data) => data.emails_queued, (data) => `${data.emails_failed} failed, bounced, or complained`],
  ["staff", "Platform users", "/staff", "bg-sky-50 text-sky-600", (data) => data.platform_users, () => "Globotech staff only"],
];

function withinRange(value, range) {
  if (!value) return false;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (range === "today") return time >= start.getTime();
  start.setDate(start.getDate() - Number(range));
  return time >= start.getTime();
}

function StatCard({ icon, label, to, tone, value, detail }) {
  return (
    <Link to={to} className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone}`}>
        <Icon name={icon} className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <span className="mt-0.5 block text-2xl font-semibold leading-none text-brand-ink">{value}</span>
        <span className="mt-1 block text-xs text-slate-500">{detail}</span>
      </span>
      <Icon name="arrow" className="h-4 w-4 text-slate-300 transition duration-200 group-hover:text-slate-500" />
    </Link>
  );
}

function Panel({ title, subtitle, action, children }) {
  return (
    <section className="flex min-h-[280px] flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        </div>
        {action}
      </div>
      <div className="mt-4 flex flex-1 flex-col">{children}</div>
    </section>
  );
}

function KioskRing({ online, offline, disabled }) {
  const total = online + offline + disabled;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const parts = [
    ["Online", online, "#16a34a"],
    ["Offline", offline, "#e11d48"],
    ["Disabled", disabled, "#94a3b8"],
  ];
  let consumed = 0;
  const segments = parts.map(([name, count, color]) => {
    const length = total > 0 ? (count / total) * circumference : 0;
    const segment = { name, count, color, length, offset: consumed };
    consumed += length;
    return segment;
  });
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 sm:flex-row">
      <div className="relative h-36 w-36">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90" role="img" aria-label={`${total} kiosks. ${online} online, ${offline} offline, ${disabled} disabled.`}>
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="12" />
          {segments.filter((segment) => segment.length > 0).map((segment) => (
            <circle key={segment.name} cx="60" cy="60" r={radius} fill="none" stroke={segment.color} strokeWidth="12" strokeDasharray={`${segment.length} ${circumference - segment.length}`} strokeDashoffset={-segment.offset} />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold">{total}</span>
          <span className="text-[11px] text-slate-500">Total kiosks</span>
        </div>
      </div>
      <ul className="space-y-2 text-sm">
        {parts.map(([name, count, color]) => (
          <li key={name} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
            <span className="w-16 text-slate-600">{name}</span>
            <span className="font-semibold">{count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Dashboard() {
  const [data, setData] = useState(null);
  const [visits, setVisits] = useState(null);
  const [error, setError] = useState("");
  const [range, setRange] = useState("today");

  useEffect(() => {
    Promise.all([loadRpc("platform_overview"), loadRpc("platform_list_visits")])
      .then(([overview, rows]) => {
        setData(overview);
        setVisits(rows || []);
      })
      .catch((err) => { setError(err.message); setVisits([]); });
  }, []);

  const ready = visits !== null;
  const filtered = useMemo(
    () => (visits || []).filter((visit) => withinRange(visit.checked_in_at || visit.created_at, range)),
    [visits, range],
  );
  const recent = filtered.slice(0, 5);
  const topSites = useMemo(() => {
    const counts = new Map();
    filtered.forEach((visit) => {
      const key = `${visit.organization} · ${visit.site}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [filtered]);
  const maxCount = topSites[0]?.[1] || 1;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Live overview of your visitor management platform. Totals are current. The date filter applies to the panels below.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="relative">
            <span className="sr-only">Date range for activity panels</span>
            <Icon name="calendar" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <select className="rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-8 text-sm font-semibold" value={range} onChange={(event) => setRange(event.target.value)}>
              {ranges.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <Link to="/clients" className="inline-flex items-center gap-1.5 rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white hover:bg-[#c4631d]">
            <Icon name="plus" className="h-4 w-4" /> Add client
          </Link>
        </div>
      </div>
      {error ? <div className="mb-4"><Notice>{error}</Notice></div> : null}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {data ? cards.map(([icon, label, to, tone, value, detail]) => (
          <StatCard key={label} icon={icon} label={label} to={to} tone={tone} value={value(data)} detail={detail(data)} />
        )) : error ? (
          <div className="sm:col-span-2 xl:col-span-3"><EmptyState icon="home" title="Couldn't load the summary" body="Refresh the page to try again." /></div>
        ) : Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="h-[88px] animate-pulse rounded-xl border border-slate-200 bg-white" />
        ))}
      </div>
      <div className="mt-4 grid gap-3 xl:grid-cols-3">
        <Panel title="Recent visits" subtitle="Latest visitor activity across all sites" action={<Link to="/visits" className="text-xs font-semibold text-brand-blue">View all</Link>}>
          {!ready ? <div className="h-40 animate-pulse rounded-lg bg-slate-50" /> : error ? <EmptyState compact icon="visits" title="Couldn't load visits" body="Refresh the page to try again." action={<EmptyAction onClick={() => window.location.reload()}>Refresh</EmptyAction>} /> : recent.length === 0 ? <EmptyState compact icon="visits" title="No visits yet" body="Visitor activity will appear here once visits begin." action={<EmptyAction to="/clients">Add a client</EmptyAction>} /> : (
            <ul className="divide-y divide-slate-100">
              {recent.map((visit) => (
                <li key={visit.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span>
                    <span className="block font-semibold">{visit.visitor_name || "Visitor"}</span>
                    <span className="text-xs text-slate-500">{visit.organization} · {visit.site}</span>
                  </span>
                  <span className="text-xs font-semibold text-slate-600">{visit.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Kiosk status" subtitle="Current status of all kiosks" action={<Link to="/kiosks" className="text-xs font-semibold text-brand-blue">View all</Link>}>
          {!ready ? <div className="h-40 animate-pulse rounded-lg bg-slate-50" /> : error || !data ? <EmptyState compact icon="kiosks" title="Couldn't load kiosk status" body="Refresh the page to try again." /> : <KioskRing online={data.kiosks_online} offline={data.kiosks_offline} disabled={data.kiosks_disabled} />}
        </Panel>
        <Panel title="Top sites by visits" subtitle="Most visited sites for the selected period" action={<Link to="/sites" className="text-xs font-semibold text-brand-blue">View all</Link>}>
          {!ready ? <div className="h-40 animate-pulse rounded-lg bg-slate-50" /> : error ? <EmptyState compact icon="sites" title="Couldn't load site activity" body="Refresh the page to try again." action={<EmptyAction onClick={() => window.location.reload()}>Refresh</EmptyAction>} /> : topSites.length === 0 ? <EmptyState compact icon="sites" title="No data yet" body="Site visit statistics will appear here once visits start." action={<EmptyAction to="/sites">View sites</EmptyAction>} /> : (
            <ul className="space-y-3">
              {topSites.map(([name, count]) => (
                <li key={name}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="truncate font-semibold">{name}</span>
                    <span className="text-slate-500">{count}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-brand-blue" style={{ width: `${(count / maxCount) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
